'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Stat {
  id: string;
  icon: string;
  value: number;
  suffix: string;
  prefix: string;
  label: string;
}

const stats: Stat[] = [
  {
    id: 'stat_quotes',
    icon: 'DocumentTextIcon',
    value: 50000,
    suffix: '+',
    prefix: '',
    label: 'Quotes Generated',
  },
  {
    id: 'stat_savings',
    icon: 'CurrencyDollarIcon',
    value: 2.4,
    suffix: 'M+',
    prefix: '$',
    label: 'Contractor Savings',
  },
  {
    id: 'stat_users',
    icon: 'UsersIcon',
    value: 1200,
    suffix: '+',
    prefix: '',
    label: 'Active Contractors',
  },
];

export default function StatsBar() {
  const [hasAnimated, setHasAnimated] = useState(false);
  const [displayValues, setDisplayValues] = useState<{ [key: string]: number }>(
    {}
  );
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            animateCounters();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasAnimated]);

  const animateCounters = () => {
    stats.forEach((stat) => {
      const duration = 1500;
      const startTime = performance.now();

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease-out-quart
        const eased = 1 - Math.pow(1 - progress, 4);
        const currentValue = stat.value * eased;

        setDisplayValues((prev) => ({
          ...prev,
          [stat.id]: currentValue,
        }));

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    });
  };

  const formatValue = (stat: Stat): string => {
    const value = displayValues[stat.id] || 0;
    const decimals = stat.value % 1 === 0 ? 0 : 1;
    return value.toFixed(decimals);
  };

  return (
    <section
      ref={sectionRef}
      className="border-y border-border bg-white/50 backdrop-blur-sm"
    >
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
          {stats.map((stat, index) => (
            <div
              key={stat.id}
              className="p-8 flex items-center gap-6 group hover:bg-white transition-colors"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all">
                <Icon name={stat.icon as any} size={24} variant="outline" />
              </div>
              <div>
                <div className="text-3xl font-mono-numbers font-bold text-foreground tracking-tight">
                  {stat.prefix}
                  {hasAnimated ? formatValue(stat) : '0'}
                  {stat.suffix}
                </div>
                <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mt-1">
                  {stat.label}
                </h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}